import React from 'react';
import { getColors } from '../theme';
import { useApp } from '../context/AppContext';
import { getConsistencyColor } from '../utils/goalConsistencyUtils';

/**
 * ConsistencyRing - Animated circular progress indicator for habit consistency
 * 
 * Features:
 * - SVG-based circular progress ring
 * - Color thresholds: 0-33% red, 34-66% yellow, 67-100% green
 * - Animations: pulse-glow at 100%, flicker at <25%
 * - Interactive mode with hover effects and click handler
 * 
 * @param {number|null} percentage - Consistency percentage 0-100, null hides ring
 * @param {number} size - Diameter of the ring in pixels (default: 64)
 * @param {number} strokeWidth - Width of the ring stroke (default: 4)
 * @param {boolean} showLabel - Whether to show percentage label inside (default: true)
 * @param {function} onClick - Click handler (optional)
 * @param {boolean} interactive - Enable hover effects and cursor pointer (default: false)
 * @param {string} className - Additional CSS classes
 */
const ConsistencyRing = ({
  percentage,
  size = 64,
  strokeWidth = 4,
  showLabel = true,
  onClick,
  interactive = false,
  className = '',
}) => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  
  // Don't render if no percentage (null = no linked habits)
  if (percentage === null || percentage === undefined) {
    return null;
  }
  
  // SVG calculations
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;
  
  // Get color and animation based on percentage
  const ringColor = getConsistencyColor(percentage, colors);
  
  // Determine if we should show the glow effect
  
  return (
    <div
      className={`relative inline-flex items-center justify-center ${interactive ? 'cursor-pointer' : ''} ${className}`}
      style={{ width: size, height: size }}
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => e.key === 'Enter' && onClick?.() : undefined}
    >
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={colors.border}
          strokeWidth={strokeWidth}
          strokeOpacity={0.3}
        />
        
        {/* Progress circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={ringColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{
            transition: 'stroke-dashoffset 0.5s ease-out, stroke 0.3s ease',
          }}
        />
      </svg>
      
      {/* Percentage label */}
      {showLabel && (
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{ color: ringColor }}
        >
          <span 
            className="font-semibold"
            style={{ 
              fontSize: size <= 24 ? '7px' : size < 40 ? '9px' : size < 60 ? '11px' : '13px',
              lineHeight: 1,
            }}
          >
            {percentage}%
          </span>
        </div>
      )}
    </div>
  );
};

export default ConsistencyRing;
